/** A menu anchored to a trigger and drawn in a portal, so a scrolling parent cannot clip it. */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/cn";

import "./Popover.css";

export interface PopoverProps {
  readonly label: ReactNode;
  /** Names the menu for a screen reader. */
  readonly menuLabel: string;
  /** Which edge of the trigger the menu grows from. */
  readonly side?: "top" | "bottom";
  readonly align?: "start" | "end";
  readonly minWidth?: number;
  readonly triggerClassName?: string;
  readonly className?: string;
  readonly children: (close: () => void) => ReactNode;
}

interface Placement {
  readonly left: number;
  readonly top: number;
  readonly width: number;
}

const GAP = 6;
const EDGE = 8;

export function Popover({
  label,
  menuLabel,
  side = "bottom",
  align = "start",
  minWidth = 260,
  triggerClassName,
  className,
  children,
}: PopoverProps) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const place = useCallback(() => {
    const anchor = trigger.current?.getBoundingClientRect();
    if (anchor === undefined) return;
    const width = Math.max(minWidth, anchor.width);
    const height = menu.current?.offsetHeight ?? 0;
    const wanted = align === "end" ? anchor.right - width : anchor.left;
    const left = Math.min(Math.max(EDGE, wanted), window.innerWidth - width - EDGE);
    const above = side === "top";
    const raw = above ? anchor.top - GAP - height : anchor.bottom + GAP;
    const top = Math.min(Math.max(EDGE, raw), window.innerHeight - height - EDGE);
    setPlacement({ left, top, width });
  }, [align, minWidth, side]);

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (trigger.current?.contains(target) === true) return;
      if (menu.current?.contains(target) === true) return;
      close();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointer);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointer);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, close, place]);

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={cn("al-popover-trigger", triggerClassName)}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => {
          setOpen((held) => !held);
        }}
      >
        {label}
      </button>
      {open
        ? createPortal(
            <div
              ref={menu}
              role="menu"
              aria-label={menuLabel}
              className={cn("al-popover", className)}
              style={{
                left: placement?.left ?? 0,
                top: placement?.top ?? 0,
                width: placement?.width ?? minWidth,
                visibility: placement === null ? "hidden" : "visible",
              }}
            >
              {children(close)}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
